import { useEffect, useState, type ComponentType } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { History, Lock, Maximize2, Minimize2, SquarePen, X, type LucideProps } from "lucide-react";
import { cn } from "@nexus/ui";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  cancelAskNexusReports,
  stopAskNexusAnswer,
  type AskNexusClarificationAnswer,
  type AskNexusConversation,
  type AskNexusTurn,
} from "@/features/ask-nexus/api/ask-nexus.api";
import { AskNexusAlert } from "@/features/ask-nexus/components/AskNexusAlert";
import type { ClarificationReply } from "@/features/ask-nexus/components/AskNexusClarification";
import { AskNexusComposer } from "@/features/ask-nexus/components/AskNexusComposer";
import { AskNexusEmptyState } from "@/features/ask-nexus/components/AskNexusEmptyState";
import { AskNexusHistory } from "@/features/ask-nexus/components/AskNexusHistory";
import { AskNexusMark } from "@/features/ask-nexus/components/AskNexusMark";
import { AskNexusChatTenantContext } from "@/features/ask-nexus/components/AskNexusNexusLink";
import { AskNexusTurnView, AskNexusUserMessage } from "@/features/ask-nexus/components/AskNexusTurnView";
import { AskNexusWorking } from "@/features/ask-nexus/components/AskNexusWorking";
import { useAskNexusAccess, useSendAskNexusMessage, useStopAskNexusAnswer } from "@/features/ask-nexus/hooks/useAskNexus";
import { useFollowLatestTurn } from "@/features/ask-nexus/hooks/useFollowLatestTurn";
import { askNexusErrorMessage } from "@/features/ask-nexus/lib/error-message";
import { ASK_NEXUS_ICON_STROKE, askNexusColumnClass } from "@/features/ask-nexus/lib/ui";
import { useAskNexusPanel } from "@/features/ask-nexus/store/ask-nexus-panel.store";
import { askNexusConversationQueryOptions } from "@/lib/queries/ask-nexus.queries";
import "@/features/ask-nexus/ask-nexus.css";

/**
 * Overlays the right of the page below the header, inset like a card, so the page
 * underneath keeps its size. On small viewports it covers the screen and hides the
 * page, still as an inset card (the prototype's phone layout). Renders nothing without access.
 */
export function AskNexusPanel() {
  const access = useAskNexusAccess();
  const isOpen = useAskNexusPanel((s) => s.isOpen);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const syncTenant = useAskNexusPanel((s) => s.syncTenant);
  // Stays mounted after close until the closing animation ends.
  const [isMounted, setIsMounted] = useState(isOpen);
  if (isOpen && !isMounted) setIsMounted(true);

  useEffect(() => {
    syncTenant(activeTenantId);
  }, [activeTenantId, syncTenant]);

  if (!access.canView || !isMounted || !activeTenantId) return null;
  return <OpenPanel key={activeTenantId} tenantId={activeTenantId} isClosing={!isOpen} onClosed={() => setIsMounted(false)} />;
}

function OpenPanel({ tenantId, isClosing, onClosed }: { tenantId: string; isClosing: boolean; onClosed: () => void }) {
  const { t } = useTranslation();
  const access = useAskNexusAccess();
  const tenantName = useAuth((s) => s.tenants.find((tenant) => tenant.id === tenantId)?.name ?? "");
  const { isWide, view, conversationId, close, toggleWide, showHistory, showChat, newChat, openConversation } = useAskNexusPanel();
  const send = useSendAskNexusMessage(tenantId, { canStop: access.canStop });
  const stop = useStopAskNexusAnswer(tenantId);
  const conversationQuery = useQuery(askNexusConversationQueryOptions(tenantId, conversationId ?? ""));
  const conversation = conversationId ? conversationQuery.data : undefined;
  const isRunning = conversation?.status === "running";
  // How many turns the chat had when the question was sent. The question stays on screen until a read
  // shows it as a turn, so it never disappears between the send returning and the next read arriving.
  const [turnsAtSend, setTurnsAtSend] = useState<number | null>(null);
  const isAwaitingSentTurn =
    send.isSuccess && turnsAtSend !== null && !conversationQuery.isError && (conversation?.turns.length ?? 0) <= turnsAtSend;
  const pendingMessage = send.isPending || isAwaitingSentTurn ? send.variables?.message ?? null : null;
  // A new composer only for a new or another chat. A new chat getting its id from the first send is the
  // same chat, so its composer, and the loader running in it, carry on.
  const [composerKey, setComposerKey] = useState(0);

  const pendingAnswers = pendingMessage !== null ? send.variables?.answers ?? null : null;
  const canAnswer = access.canSend && conversation?.status === "idle" && !send.isPending && pendingMessage === null;

  const handleSend = (message: string, answers?: AskNexusClarificationAnswer[]) => {
    stop.reset();
    setTurnsAtSend(conversation?.turns.length ?? 0);
    send.mutate({ message, conversationId: conversationId ?? undefined, ...(answers ? { answers } : {}) });
  };
  const handleNewChat = () => {
    send.reset();
    setTurnsAtSend(null);
    stop.reset();
    setComposerKey((key) => key + 1);
    newChat();
  };
  const handleOpen = (id: string) => {
    send.reset();
    setTurnsAtSend(null);
    stop.reset();
    setComposerKey((key) => key + 1);
    openConversation(id);
  };

  const requestError = send.error ?? stop.error ?? (conversationId ? conversationQuery.error : null);

  useStopWorkOnClose({ isClosing, conversationId, conversation, canStop: access.canStop, canGenerateReport: access.canGenerateReport });

  return (
    <aside
      aria-label={t("askNexus.title", "Ask Nexus")}
      data-testid="ask-nexus-panel"
      data-ask-nexus=""
      onAnimationEnd={(event) => {
        if (isClosing && event.target === event.currentTarget) onClosed();
      }}
      className={cn(
        "fixed inset-0 z-(--z-modal) flex bg-background p-3",
        isClosing ? "ask-nexus-panel-exit" : "ask-nexus-panel-enter",
        "md:inset-auto md:top-[72px] md:right-4 md:bottom-4 md:z-(--z-sheet) md:min-w-[400px] md:bg-transparent md:p-0",
        isWide ? "md:w-[calc(60vw-16px)]" : "md:w-[calc(min(576px,44vw)-16px)]",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col overflow-clip rounded-[24px] border border-(--an-card-hair) bg-(--an-panel-fill) text-sm leading-normal text-(--an-ink) shadow-(--an-sh-panel) backdrop-blur-[24px] backdrop-saturate-[1.1]">
        <header className="flex h-16 flex-none items-center gap-2 border-b border-(--an-hair) pr-2 pl-3.5 md:pr-3 md:pl-5">
          <h2 className="flex items-center gap-2 text-base font-medium tracking-[-0.015em] whitespace-nowrap">
            <AskNexusMark className="h-3.5 w-[26px] flex-none" />
            {view === "history" ? t("askNexus.actions.history", "History") : t("askNexus.title", "Ask Nexus")}
          </h2>
          <span
            className="hidden h-7 min-w-0 items-center gap-1.5 rounded-full bg-(--an-neutral-tint) pr-2.5 pl-2 text-xs font-medium whitespace-nowrap md:inline-flex"
            title={t("askNexus.tenantChip", "Answers use {{tenant}} data only", { tenant: tenantName })}
            data-testid="ask-nexus-tenant"
          >
            <Lock className="size-3 flex-none text-(--an-ink-2)" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
            <span className="truncate">{tenantName}</span>
          </span>
          <div className="ml-auto flex gap-0.5">
            <HeaderButton icon={SquarePen} label={t("askNexus.actions.newChat", "New chat")} onClick={handleNewChat} />
            <HeaderButton
              icon={History}
              label={t("askNexus.actions.history", "History")}
              isPressed={view === "history"}
              onClick={view === "history" ? showChat : showHistory}
            />
            <HeaderButton
              icon={isWide ? Minimize2 : Maximize2}
              label={isWide ? t("askNexus.actions.collapse", "Collapse") : t("askNexus.actions.expand", "Expand")}
              className="hidden md:grid"
              onClick={toggleWide}
            />
            <HeaderButton icon={X} label={t("askNexus.actions.close", "Close")} onClick={close} />
          </div>
        </header>

        <AskNexusChatTenantContext.Provider value={tenantId}>
          {view === "history" ? (
            <AskNexusHistory tenantId={tenantId} tenantName={tenantName} activeConversationId={conversationId} onOpen={handleOpen} />
          ) : (
            <>
              <ChatBody
                conversation={conversation}
                isLoading={conversationId !== null && conversationQuery.isPending && pendingMessage === null}
                pendingMessage={pendingMessage}
                pendingAnswers={pendingAnswers}
                canAnswer={canAnswer}
                tenantName={tenantName}
                canSend={access.canSend}
                onAsk={handleSend}
              />
              <footer className="flex-none px-4 pb-5">
                <div className={cn("mx-auto", askNexusColumnClass(isWide))}>
                  <ConversationClosedAlert conversation={conversation} onNewChat={handleNewChat} />
                  {requestError ? <AskNexusAlert tone="bad" className="mb-2" title={askNexusErrorMessage(t, requestError, tenantName)} /> : null}
                  <AskNexusComposer
                    key={composerKey}
                    tenantName={tenantName}
                    canSend={access.canSend}
                    canStop={access.canStop}
                    isRunning={isRunning}
                    // Until a read shows the sent question, it is still on its way: the loader keeps running.
                    isSending={pendingMessage !== null}
                    isStopping={stop.isPending}
                    disabledReason={closedReason(t, conversation)}
                    footnote={closedFootnote(t, conversation)}
                    onSend={handleSend}
                    onStop={() => conversationId && stop.mutate(conversationId)}
                  />
                </div>
              </footer>
            </>
          )}
        </AskNexusChatTenantContext.Provider>
      </div>
    </aside>
  );
}

/**
 * Closing the panel stops what this chat still has running on the server: the answer, through Stop naming that
 * answer, and the reports in progress, through the report cancel. Only stopping the polling would leave the agent
 * runs going after the panel is gone. A Stop sent on close always names its answer, so a late one never stops a newer
 * answer; while the running answer's id is not known yet, the send that started it stops it once it returns.
 * Closing the browser tab or switching organization sends neither.
 */
function useStopWorkOnClose({
  isClosing,
  conversationId,
  conversation,
  canStop,
  canGenerateReport,
}: {
  isClosing: boolean;
  conversationId: string | null;
  conversation: AskNexusConversation | undefined;
  canStop: boolean;
  canGenerateReport: boolean;
}) {
  const runningMessageId = isClosing && canStop && conversation?.status === "running" ? (conversation.runningMessageId ?? null) : null;
  const requestIds = isClosing && canGenerateReport ? reportRequestsInProgress(conversation) : [];
  const requestKey = requestIds.join(",");
  useEffect(() => {
    if (!runningMessageId || !conversationId) return;
    // Fire and forget: the panel is going away. A 409 means that answer already ended.
    stopAskNexusAnswer(conversationId, runningMessageId).catch(() => undefined);
  }, [runningMessageId, conversationId]);
  useEffect(() => {
    if (!requestKey || !conversationId) return;
    // A cleanup step Nexus could not finish stays pending on its side for the next read or cancel.
    cancelAskNexusReports(conversationId, requestKey.split(",")).catch(() => undefined);
  }, [requestKey, conversationId]);
}

/** The requests whose report is running, or whose report the card may still be starting. */
function reportRequestsInProgress(conversation: AskNexusConversation | undefined): string[] {
  return (conversation?.turns ?? []).flatMap((turn) =>
    (turn.blocks ?? []).flatMap((block) => {
      if (block.type !== "card" || block.card.card !== "report_request" || block.card.requestId === null) return [];
      const isInProgress = block.card.report === null || block.card.report.status === "running";
      return isInProgress ? [block.card.requestId] : [];
    }),
  );
}

function closedReason(t: ReturnType<typeof useTranslation>["t"], conversation: AskNexusConversation | undefined): string | null {
  if (conversation?.status === "limit_reached") {
    return t("askNexus.composer.limitReached", "This chat reached its limit. Start a new chat to keep asking.");
  }
  if (conversation?.status === "failed") {
    return t("askNexus.composer.failed", "This chat can't continue. Start a new chat.");
  }
  return null;
}

/** The prototype's footnote under a closed chat says why, instead of the data reminder. */
function closedFootnote(t: ReturnType<typeof useTranslation>["t"], conversation: AskNexusConversation | undefined): string | null {
  if (conversation?.status === "limit_reached") {
    return t("askNexus.composer.limitFootnote", "Each conversation has a limit. A new one starts fresh.");
  }
  if (conversation?.status === "failed") {
    return t("askNexus.composer.failedFootnote", "This conversation can't continue. A new one starts fresh.");
  }
  return null;
}

function ConversationClosedAlert({ conversation, onNewChat }: { conversation: AskNexusConversation | undefined; onNewChat: () => void }) {
  const { t } = useTranslation();
  if (conversation?.status !== "limit_reached" && conversation?.status !== "failed") return null;
  return (
    <AskNexusAlert
      tone="bad"
      className="mb-2"
      title={
        conversation.status === "limit_reached"
          ? t("askNexus.banner.limitReached", "This chat reached its limit.")
          : t("askNexus.banner.failed", "This chat stopped because of an error.")
      }
      description={t("askNexus.banner.startNew", "Start a new chat to keep asking.")}
      action={
        <button
          type="button"
          className="inline-flex h-8 items-center gap-2 rounded-full bg-(--an-primary) px-3 text-xs font-medium text-(--an-primary-ink)"
          onClick={onNewChat}
        >
          <SquarePen className="size-3.5" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
          {t("askNexus.actions.newChat", "New chat")}
        </button>
      }
    />
  );
}

function ChatBody({
  conversation,
  isLoading,
  pendingMessage,
  pendingAnswers,
  canAnswer,
  tenantName,
  canSend,
  onAsk,
}: {
  conversation: AskNexusConversation | undefined;
  isLoading: boolean;
  pendingMessage: string | null;
  pendingAnswers: AskNexusClarificationAnswer[] | null;
  canAnswer: boolean;
  tenantName: string;
  canSend: boolean;
  onAsk: (message: string, answers?: AskNexusClarificationAnswer[]) => void;
}) {
  const { t } = useTranslation();
  const isWide = useAskNexusPanel((s) => s.isWide);
  const turns = conversation?.turns ?? [];
  const latestKey = isLoading ? "loading" : `${conversation?.conversationId ?? "new"}:${turns.length}:${pendingMessage ?? ""}`;
  const { threadRef, latestRef, stopFollowing } = useFollowLatestTurn(latestKey);

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <AskNexusWorking text={t("askNexus.loading", "Loading chat…")} />
      </div>
    );
  }
  if (turns.length === 0 && !pendingMessage) {
    return (
      <div className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
        <AskNexusEmptyState tenantName={tenantName} canSend={canSend} onAsk={onAsk} />
      </div>
    );
  }
  return (
    <div
      className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto scroll-smooth [scrollbar-gutter:stable_both-edges]"
      ref={threadRef}
      data-testid="ask-nexus-thread"
      onWheel={stopFollowing}
      onTouchMove={stopFollowing}
      onPointerDown={stopFollowing}
      onKeyDown={stopFollowing}
    >
      <div className={cn("mx-auto px-3.5 pt-4 pb-3 md:px-6 md:pt-6 md:pb-4", askNexusColumnClass(isWide))}>
        {conversation?.isHistoryTruncated ? (
          <p className="mb-6 text-center text-xs text-(--an-ink-3)">
            {t("askNexus.turn.earlierHidden", "Earlier messages in this chat aren't shown.")}
          </p>
        ) : null}
        {turns.map((turn, index) => (
          <div key={index} ref={index === turns.length - 1 && !pendingMessage ? latestRef : undefined}>
            <AskNexusTurnView
              turn={turn}
              reply={{ answered: answeredBy(turns[index + 1], index === turns.length - 1 ? pendingMessage : null, pendingAnswers), canAnswer, onAnswer: onAsk }}
              onFollowUp={index === turns.length - 1 && canAnswer ? onAsk : undefined}
            />
          </div>
        ))}
        {pendingMessage ? (
          <div ref={latestRef} className="mb-6">
            <AskNexusUserMessage text={pendingMessage} />
            <AskNexusWorking className="mt-4" text={t("askNexus.turn.sending", "Sending your question")} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function HeaderButton({
  icon: IconComponent,
  label,
  isPressed,
  className,
  onClick,
}: {
  icon: ComponentType<LucideProps>;
  label: string;
  isPressed?: boolean;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={isPressed}
      className={cn(
        "grid size-8 place-items-center rounded-full text-(--an-ink-nav) transition-colors hover:bg-(--an-inset-hover) hover:text-(--an-ink) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--an-ink-3)",
        isPressed && "bg-(--an-inset-hover) text-(--an-ink)",
        className,
      )}
      onClick={onClick}
    >
      <IconComponent className="size-4" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
    </button>
  );
}

/** A card is answered once a later message exists, or one is on its way; only then can it never be sent again. */
function answeredBy(
  next: AskNexusTurn | undefined,
  pendingMessage: string | null,
  pendingAnswers: AskNexusClarificationAnswer[] | null,
): ClarificationReply["answered"] {
  if (next) return { answers: next.clarificationAnswers ?? null };
  return pendingMessage !== null ? { answers: pendingAnswers } : null;
}
